import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { Router } from "@angular/router";
import { AppConfigService } from './app-config.service';
import { TrackDataModel } from './model/TrackDataModel';
import { AddTrackResultModel, CargoModel, ShipmentDetailsModel, ShipmentsDataModel, Track4ShipmentModel, TrackDataHistoryModel } from './model';
import { AbstractDataService } from './abstract-data-service';
import { AuthService } from './auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class DataService extends AbstractDataService {

    constructor(http: HttpClient, router: Router, config: AppConfigService, private auth: AuthService) {
        super(http, router, config);
    }

    /**
     * Return an oferta text for the cargo 
     * 
     * @param cargoId 
     * @returns 
     */
    public getOferta(cargoId: number): Observable<String> {
        return this.requestData(`app/oferta/${cargoId}`, {}, 'get');
    }

    /**
     * Get client tracks
     * @returns a list of client tracks
     */
    public userTracks(): Observable<TrackDataModel[]> {
        return this.auth.requestSecuredData('app/userTracks', {}, 'get');
    }

    /**
     * Return a list of history items by given track id
     * 
     * @param trackId track id for history
     * @returns a list of history items
     */
    public trackHistory(trackId: number): Observable<TrackDataHistoryModel[]> {
        return this.auth.requestSecuredData('app/trackHistory', { track_id: trackId }, 'get');
    }

    /**
     * Add track by client. This is only for client.
     * 
     * @param tracknum track number
     * @param description track description
     * @param override_track override existing track or return an error 
     * @returns created track id
     */
    public addClientTrack(tracknum: string, description?: string, override_track: boolean = false): Observable<number> {
        return this.auth.requestSecuredData('app/addUserTrack', { tracknum, description, override_track }, 'post');
    }

    /**
     * Delete customer track
     * 
     * @param trackId 
     * @returns 
     */
    public deleteCustomerTrack(trackId: number): Observable<any> {
        return this.auth.requestSecuredData('app/deleteUserTrack', { track_id: trackId }, 'delete');
    }

    /**
     * Get cargo information page data
     * 
     * @returns 
     */
    public cargoInfo(): Observable<any> {
        return this.auth.requestSecuredData('app/info', {}, 'get');
    }

    /**
     * Add track by storage
     * 
     * @param tracknum 
     * @returns 
     */
    public addStorageTrack(tracknum: string, weight?: number, client_id?: number): Observable<AddTrackResultModel> {
        return this.auth.requestSecuredData("app/addStorageTrack", { tracknum, weight, client_id }, 'post');
    }

    /**
     * Bulk receiving tracks by storage
     * 
     * @param tracksData list of tracks
     * @param packageDate date of package receiving
     * @param storageId id of destination storage
     * @returns 
     */
    public addStorageTracksBulk(tracksData: any, packageDate: number, storageId?: number): Observable<any> {
        return this.auth.requestSecuredData('app/addStorageTracksBulk', {
            tracks_data: tracksData,
            package_date: packageDate,
            storage_id: storageId,
        }, 'post')
    }

    /**
     * Get list of storages
     */
    public storages(): Observable<any> {
        return this.auth.requestSecuredData('admin/storages', {}, 'get');
    }


    /**
     * Get shipments list for given storeId
     * 
     * @param storeId 
     * @returns 
     */
    public shipmentsData(storeId: number): Observable<ShipmentsDataModel[]> {
        return this.auth.requestSecuredData(`admin/shipmentsData/${storeId}`, {}, 'get');
    }

    /**
     * Get full list of clients
     * 
     * @returns list of cients
     */
    public clientsData(): Observable<any> {
        return this.auth.requestSecuredData('reports/clientsData', {}, 'get');
    }

    /**
     * Daily statistics report
     * 
     * @param timestamp 
     * @param statType 
     * @returns 
     */
    public dailyStatistic(timestamp: number, statType: number) {
        return this.auth.requestSecuredData('reports/dailyStatistic', { date: timestamp, type: statType }, 'get');
    }

    /**
     * Daily distribution statistics 
     * 
     * @param date 
     * @returns 
     */
    public dailyDistributionStatistic(timestamp: number) {
        return this.auth.requestSecuredData('reports/dailyDistributionStatistic', { date: timestamp }, 'get');
    }

    /**
     * Get detailed information about track
     * 
     * @param tracknum tracknumber for search
     * @returns detailed track information
     */
    public trackInfo(tracknum: string) {
        return this.auth.requestSecuredData('admin/trackInfo', { tracknum: tracknum }, 'get');
    }

    /**
     * Get tracks for given shipment
     * 
     * @param shipmentId shipment ID
     * @returns 
     */
    public getShipmentDetails(shipmentId: number): Observable<ShipmentDetailsModel[]> {
        return this.auth.requestSecuredData(`admin/shipmentDetails/${shipmentId}`, {}, 'get');
    }

    /**
     * Get tracks for manual shipment
     * 
     * @param fd first date
     * @param td last date
     * @param storeId store ID
     * @returns 
     */
    public packagesForShipment(fd: number, td: number, storeId: number): Observable<Track4ShipmentModel[]> {
        return this.auth.requestSecuredData('admin/packagesForManualShipment', { fd: fd, td: td, storeId: storeId }, 'get');
    }

    /**
     * Get list of storagies for manual shipment
     * 
     * @param fd first date
     * @param td last date
     * @param storeId store ID
     * @returns 
     */
    public storagiesForShipment(fd: number, td: number, storeId: number): Observable<any> {
        return this.auth.requestSecuredData('admin/storagiesForManualShipment', { fd: fd, td: td, storeId: storeId }, 'get');
    }

    /**
     * Make shipment for given storeId and destStoreID (if needed)
     * 
     * @param fd 
     * @param td 
     * @param storeId 
     * @param destStorageId 
     * @returns 
     */
    public makeShipment(fd: number, td: number, storeId: number, destStorageId: number | null): Observable<any> {
        return this.auth.requestSecuredData('admin/makeShipment', { fd: fd, td: td, store_id: storeId, dest_storage_id: destStorageId }, 'post');
    }


    /**
     * Receive a track in destination storage
     * 
     * @param params 
     * @returns 
     */
    public trackReceived(params: { tracknum: string, create_track: boolean, package_weight?: number, package_date?: number, client_id?: number}): Observable<any> {
        return this.auth.requestSecuredData('admin/trackReceived', params, 'post');
    }

    /**
     * Get list of all client for given cargo
     * 
     * @returns 
     */
    public clientNamesList() {
        return this.auth.requestSecuredData('admin/clientNames', {}, 'get');
    }

    /**
     * Get all tracks for client
     * 
     * @param client_id 
     * @returns 
     */
    public clientTracks(client_id: number) {
        return this.auth.requestSecuredData('admin/clientTracks', { client_id: client_id }, 'get');
    }

    /**
     * Delivery package to a client
     * 
     * @param clientId client id 
     * @param tracknum track number
     * @param comment track comment
     * @param forceDelivery deliver the package even if the package is not belongs to the client
     * @returns 
     */
    public makePackageDelivered(clientId: number, tracknum: string, comment: string, forceDelivery: boolean) {
        return this.auth.requestSecuredData('admin/makeDelivered', { client_id: clientId, tracknum: tracknum, comment: comment, force_delivery: forceDelivery }, 'post');
    }

    
    /**
     * Delete status (history record) by HistoryId
     * 
     * @param history_id 
     * @returns "ok" if everything fine
     */
    public removePackageHistory(history_id: number) {
        return this.auth.requestSecuredData('admin/deletePackageHistory', { history_id }, 'delete');
    }

    /**
     * Get Client information by ClientId
     * 
     * @param clientId 
     * @returns 
     */
    public clientInfoById(user_id: number) {
        return this.auth.requestSecuredData('admin/userById', { user_id }, 'get');
    }


    /**
     * Restore deleted track by track id
     * 
     * @param tracknumId 
     * @returns 
     */
    public restoreTrack(tracknumId: number) {
        return this.auth.requestSecuredData('admin/restoreTrack', { id: tracknumId }, 'get');
    }

    /**
     * Ships selected tracks without shipment record creating
     * 
     * @param fd 
     * @param td 
     * @param storeId 
     * @param tracks 
     * @returns 
     */
    public shipSelectedPackages(fd: any, td: any, storeId: number, tracks: number[]): Observable<any> {
        return this.auth.requestSecuredData('admin/shipSelectedPackages', { fd: fd, td: td, store_id: storeId, tracks: tracks }, 'post');
    }


    // su panel is not ready yet
    public getCargoData(): Observable<CargoModel[]> {
        return this.requestData('su/getCargoData', {}, 'post');
    }

    // public checkTracks(trackNums: string[]): Observable<any> {
    //     return this.requestData('app/checkTracks', { trackNums: trackNums }, 'post');
    // }


    // public addTracksBulk(params: { tracksData: string, packageDate: number, storageId: number | null }): Observable<any> {
    //     return this.requestData('app/addTracksBulk', params, 'post');
    // }

    // public addTracksBulkWithWeight(params: { tracksData: string, packageDate: number, storageId: number | null }): Observable<any> {
    //     return this.requestData('app/addTracksBulk2', params, 'post');
    // }

    // public editTrack(params: { originalTrackNum: string, tracknum: string, title: string, comment: string }): Observable<any> {
    //     return this.requestData('app/editTrack', params, 'put');
    // }

    // public addAsCustomerTrack(trackId: number): Observable<any> {
    //     return this.requestData('app/addAsMyTrack', { trackId: trackId }, 'put');
    // }

    // public userContacts() {
    //     return this.requestData('admin/userContacts', {}, 'get');
    // }

    // public cargoStoragies(): Observable<any> {
    //     return this.requestData("admin/cargoStoragies", {}, 'get');
    // }
}
